// =====================================================================
// Supabase client, lazily instantiated.
// ---------------------------------------------------------------------
// The Supabase SDK is ~207 KB minified. Keeping a static `import` at
// the top of this file would force every page (including the landing
// page, which doesn't really need auth to render) to download and
// parse the SDK before React paints anything.
//
// Instead we defer the import until the first caller asks for the
// client. Vite splits `@supabase/supabase-js` into its own chunk
// (`vendor-supabase`) and the browser only fetches it when:
//   • <AuthProvider> kicks off its background session refresh, OR
//   • a route guard / sign-in form actually needs it.
//
// Reads the public URL + anon key from env vars (safe to ship in the
// browser bundle, RLS is what enforces access).
//
// Accepts either naming convention:
//   • VITE_SUPABASE_URL        / VITE_SUPABASE_ANON_KEY        (Vite default)
//   • NEXT_PUBLIC_SUPABASE_URL / NEXT_PUBLIC_SUPABASE_ANON_KEY (Next.js style)
// Both prefixes are exposed to the bundle via `envPrefix` in
// vite.config.js. Set ONE pair, not both, VITE_* wins if both are
// present.
// =====================================================================

const url =
  import.meta.env.VITE_SUPABASE_URL ||
  import.meta.env.NEXT_PUBLIC_SUPABASE_URL;
const anonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  import.meta.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // We don't throw here so dev mode without a configured .env still
  // boots; auth calls will fail loudly when invoked.
  // eslint-disable-next-line no-console
  console.warn(
    "[supabase] Missing Supabase URL or anon key. Set either " +
      "VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY or " +
      "NEXT_PUBLIC_SUPABASE_URL + NEXT_PUBLIC_SUPABASE_ANON_KEY. " +
      "Copy .env.example to .env and fill in real values."
  );
}

let _clientPromise = null;

/**
 * Returns a Promise that resolves to the singleton Supabase client.
 * First call triggers a dynamic import of `@supabase/supabase-js`
 * (downloads the vendor-supabase chunk if not already cached);
 * subsequent calls reuse the same promise.
 */
export function getSupabase() {
  if (!_clientPromise) {
    _clientPromise = import("@supabase/supabase-js").then(({ createClient }) =>
      createClient(url ?? "", anonKey ?? "", {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true, // picks up OAuth redirects
        },
      })
    );
  }
  return _clientPromise;
}

// Convenience: the Supabase URL is needed for <link rel="preconnect">
// hints and CSP headers. Exporting it from the same module lets
// callers reach for the URL without instantiating the client.
export const supabaseUrl = url ?? "";
